import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Check, X, ChevronDown, ChevronUp, UserMinus, PenLine, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchGroupMembersWithBetStatus, kickMember, sendBetReminder, type MemberBetStatus } from '@/lib/supabase-queries';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { UserAvatar } from '@/components/UserAvatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface MemberBetStatusListProps {
  groupId: string;
  seriesList?: any[];
  footerLink?: { label: string; to: string };
}

const statusDot = (filled: number, total: number) => {
  if (total === 0) return 'bg-gray-500';
  if (filled === 0) return 'bg-red-500';
  if (filled >= total) return 'bg-green-500';
  return 'bg-orange-500';
};

export function MemberBetStatusList({ groupId, seriesList = [], footerLink }: MemberBetStatusListProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [members, setMembers] = useState<MemberBetStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [kickTarget, setKickTarget] = useState<MemberBetStatus | null>(null);
  const [kicking, setKicking] = useState(false);

  // Email reminder states
  const [sendingEmailFor, setSendingEmailFor] = useState<string | null>(null);
  const [emailSentFor, setEmailSentFor] = useState<Set<string>>(new Set());
  const [globalEmailSent, setGlobalEmailSent] = useState(false);
  const [sendingGlobalEmail, setSendingGlobalEmail] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await fetchGroupMembersWithBetStatus(groupId);
      setMembers(data);
      setLoading(false);
    };
    load();
  }, [groupId]);

  const activeSeries = seriesList.filter(s => s.status === 'upcoming' || s.status === 'in_progress');
  const nonBetterCount = members.filter(m => m.hasPending).length;
  const pendingUserIds = members.filter(m => m.hasPending).map(m => m.userId);

  const toggleExpand = (userId: string) => {
    setExpandedUserId(prev => (prev === userId ? null : userId));
  };

  const handleKickConfirm = async () => {
    if (!kickTarget) return;
    setKicking(true);
    try {
      await kickMember(kickTarget.userId, groupId);
      setMembers(prev => prev.filter(m => m.userId !== kickTarget.userId));
      toast({ title: `${kickTarget.displayName} הוסר מהקבוצה` });
    } catch (err: any) {
      toast({ title: 'שגיאה', description: err.message, variant: 'destructive' });
    }
    setKicking(false);
    setKickTarget(null);
  };

  const handleSendEmailReminder = async (userId: string, displayName: string) => {
    setSendingEmailFor(userId);
    try {
      await sendBetReminder(groupId, [userId]);
      toast({ title: `תזכורת נשלחה ל-${displayName}` });
      setEmailSentFor(prev => new Set(prev).add(userId));
    } catch (err: any) {
      toast({ title: 'שגיאה בשליחת תזכורת', description: err.message, variant: 'destructive' });
    }
    setSendingEmailFor(null);
  };

  const handleSendGlobalEmailReminder = async () => {
    setSendingGlobalEmail(true);
    try {
      const result = await sendBetReminder(groupId, pendingUserIds);
      toast({ title: `נשלחו ${result.sent} תזכורות` });
      setGlobalEmailSent(true);
      setEmailSentFor(prev => {
        const next = new Set(prev);
        pendingUserIds.forEach(id => next.add(id));
        return next;
      });
    } catch (err: any) {
      toast({ title: 'שגיאה בשליחת תזכורות', description: err.message, variant: 'destructive' });
    }
    setSendingGlobalEmail(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div>
      {/* Header with global email button */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold tracking-tight">חברי הקבוצה</h2>
        {nonBetterCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleSendGlobalEmailReminder}
            disabled={globalEmailSent || sendingGlobalEmail}
            className="text-xs gap-1.5 border-accent/30 hover:border-accent"
          >
            {sendingGlobalEmail ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : globalEmailSent ? (
              <Check className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <Mail className="w-3.5 h-3.5" />
            )}
            <span>שלח תזכורת מייל ({nonBetterCount})</span>
          </Button>
        )}
      </div>

      {/* Member list */}
      <div className="space-y-2">
        {members.map((member, index) => {
          const isExpanded = expandedUserId === member.userId;
          const isSelf = member.userId === user?.id;
          const isEmailSending = sendingEmailFor === member.userId;
          const isEmailSent = emailSentFor.has(member.userId);
          return (
            <motion.div
              key={member.userId}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04 }}
            >
              <div className="bg-card rounded-2xl border border-border transition-colors hover:border-accent/30">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpand(member.userId)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleExpand(member.userId); }}
                  className="w-full p-4 text-right cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${statusDot(member.totalFilled, member.totalOpen)}`} />
                      <UserAvatar avatarId={member.avatarId} displayName={member.displayName} size="sm" />
                      <div>
                        <span className="font-medium text-sm">{member.displayName}</span>
                        {member.role === 'commissioner' && (
                          <span className="text-[10px] text-accent mr-2 font-medium uppercase tracking-wide">קומישינר</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {member.hasPending && (
                        isEmailSent ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendEmailReminder(member.userId, member.displayName);
                            }}
                            disabled={isEmailSending}
                            className="p-1 rounded hover:bg-accent/10 transition-colors"
                            title={`שלח תזכורת ל-${member.displayName}`}
                          >
                            {isEmailSending ? (
                              <Loader2 className="w-4 h-4 animate-spin text-accent" />
                            ) : (
                              <Mail className="w-4 h-4 text-accent" />
                            )}
                          </button>
                        )
                      )}
                      <span className="text-sm text-muted-foreground" dir="ltr">
                        {member.totalFilled}/{member.totalOpen} הימורים
                      </span>
                      {!member.hasPending ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-4 pb-4 border-t border-border"
                  >
                    {/* Series section */}
                    {activeSeries.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {activeSeries.map(s => {
                          const hasBet = member.bettedSeriesIds.includes(s.id);
                          const isCommEdited = member.commissionerEditedSeriesIds.includes(s.id);
                          const seriesLabel = `${s.team_home || '?'} vs ${s.team_away || '?'}`;
                          return (
                            <div key={s.id} className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">{seriesLabel}</span>
                              <div className="flex items-center gap-2">
                                {hasBet ? (
                                  <>
                                    <Check className="w-3.5 h-3.5 text-green-500" />
                                    {isCommEdited && (
                                      <span className="text-amber-400" title="נערך ע״י קומישינר">
                                        <PenLine className="w-3 h-3" />
                                      </span>
                                    )}
                                    <Link
                                      to={`/group/${groupId}/series/${s.id}?godmode=${member.userId}`}
                                      className="text-accent hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      ערוך הימור
                                    </Link>
                                  </>
                                ) : (
                                  <>
                                    <X className="w-3.5 h-3.5 text-red-500" />
                                    <Link
                                      to={`/group/${groupId}/series/${s.id}?godmode=${member.userId}`}
                                      className="text-accent border border-accent/30 rounded px-1.5 py-0.5 hover:bg-accent/10"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      הנח הימור
                                    </Link>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Side bets section */}
                    {member.totalSideBets > 0 && (
                      <>
                        <div className="border-t border-border my-2" />
                        <span className="text-xs text-muted-foreground font-medium">הימורי צד</span>
                        <div className="mt-1.5 space-y-1">
                          {member.pendingSideBetNames.length > 0 ? (
                            member.pendingSideBetNames.map((name, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                                <span className="text-muted-foreground">{name}</span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center gap-2 text-xs">
                              <Check className="w-3.5 h-3.5 text-green-500" />
                              <span className="text-muted-foreground">כל הימורי הצד מולאו</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Kick button */}
                    {!isSelf && (
                      <div className="mt-3 pt-3 border-t border-border flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setKickTarget(member);
                          }}
                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-400 transition-colors"
                        >
                          <UserMinus className="w-3.5 h-3.5" />
                          הסר
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            </motion.div>
          );
        })}

        {members.length === 0 && (
          <p className="text-muted-foreground text-sm text-center py-8">אין חברים בקבוצה</p>
        )}
      </div>

      {/* Footer link */}
      {footerLink && (
        <div className="mt-4 text-center">
          <Link
            to={footerLink.to}
            className="text-sm text-accent hover:underline"
          >
            {footerLink.label}
          </Link>
        </div>
      )}

      {/* Kick confirmation dialog */}
      <Dialog open={!!kickTarget} onOpenChange={(open) => !open && setKickTarget(null)}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>הסרת חבר מהקבוצה</DialogTitle>
            <DialogDescription className="space-y-2 text-right">
              <span className="block">
                להסיר את <strong>{kickTarget?.displayName}</strong> מהקבוצה?
              </span>
              <span className="block text-sm text-muted-foreground">
                הגישה לקבוצה תוסר. ההימורים והניקוד יישמרו בהיסטוריה. לא ניתן לבטל.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:flex-row-reverse">
            <Button
              variant="destructive"
              onClick={handleKickConfirm}
              disabled={kicking}
              className="gap-1"
            >
              {kicking ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />}
              הסר
            </Button>
            <Button
              variant="outline"
              onClick={() => setKickTarget(null)}
              disabled={kicking}
            >
              ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MemberBetStatusList;
